import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../../api/client';
import { useAuth } from '../../../contexts/AuthContext.jsx';
import DiscussThread from '../../messenger/DiscussThread.jsx';

const TABS = ['overview', 'invitations', 'bids', 'scoreboard', 'award'];

const EMPTY_INVITE_FORM = { supplierId: '', supplierName: '', email: '' };
const EMPTY_BID_FORM    = { supplierId: '', amountCents: '', notes: '' };
const EMPTY_AWARD_FORM  = { bidId: '', note: '' };

export default function EventWorkspacePage() {
  const { id } = useParams();
  const { can } = useAuth();
  const [tab,          setTab]          = useState('overview');
  const [event,        setEvent]        = useState(null);
  const [invitations,  setInvitations]  = useState([]);
  const [bids,         setBids]         = useState([]);
  const [scoreboard,   setScoreboard]   = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);

  // Invite form
  const [inviteOpen,   setInviteOpen]   = useState(false);
  const [inviteForm,   setInviteForm]   = useState(EMPTY_INVITE_FORM);
  const [inviteSaving, setInviteSaving] = useState(false);

  // Bid form
  const [bidOpen,      setBidOpen]      = useState(false);
  const [bidForm,      setBidForm]      = useState(EMPTY_BID_FORM);
  const [bidSaving,    setBidSaving]    = useState(false);

  // Award confirm modal
  const [awardOpen,    setAwardOpen]    = useState(false);
  const [awardForm,    setAwardForm]    = useState(EMPTY_AWARD_FORM);
  const [awardSaving,  setAwardSaving]  = useState(false);

  // Scoreboard loading
  const [scoreLoading, setScoreLoading] = useState(false);

  // Status transition
  const [transitioning, setTransitioning] = useState(false);

  async function loadEvent() {
    try {
      const { data } = await api.get(`/sourcing/events/${id}`);
      setEvent(data.data || data);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    }
  }

  async function loadInvitations() {
    try {
      const { data } = await api.get(`/sourcing/events/${id}/invitations`);
      setInvitations(data.data?.items || []);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    }
  }

  async function loadBids() {
    try {
      const { data } = await api.get(`/sourcing/events/${id}/bids`);
      setBids(data.data?.items || []);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    }
  }

  async function loadScoreboard() {
    setScoreLoading(true);
    try {
      const { data } = await api.get(`/sourcing/events/${id}/scoreboard`);
      setScoreboard(data.data?.items || []);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setScoreLoading(false);
    }
  }

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    await Promise.all([loadEvent(), loadInvitations(), loadBids(), loadScoreboard()]);
    setLoading(false);
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadAll(); }, [loadAll]);

  // --- Invite supplier ---
  function patchInvite(key, val) { setInviteForm((f) => ({ ...f, [key]: val })); }

  async function handleInvite(e) {
    e.preventDefault();
    setInviteSaving(true);
    setError(null);
    try {
      await api.post(`/sourcing/events/${id}/invitations`, inviteForm);
      setInviteForm(EMPTY_INVITE_FORM);
      setInviteOpen(false);
      await loadInvitations();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setInviteSaving(false);
    }
  }

  // --- Record bid ---
  function patchBid(key, val) { setBidForm((f) => ({ ...f, [key]: val })); }

  async function handleBid(e) {
    e.preventDefault();
    setBidSaving(true);
    setError(null);
    try {
      await api.post(`/sourcing/events/${id}/bids`, {
        supplierId:  bidForm.supplierId,
        amountCents: Number(bidForm.amountCents),
        notes:       bidForm.notes,
      });
      setBidForm(EMPTY_BID_FORM);
      setBidOpen(false);
      await Promise.all([loadBids(), loadScoreboard()]);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setBidSaving(false);
    }
  }

  // --- Award ---
  function patchAward(key, val) { setAwardForm((f) => ({ ...f, [key]: val })); }

  async function handleAward(e) {
    e.preventDefault();
    setAwardSaving(true);
    setError(null);
    try {
      await api.post(`/sourcing/events/${id}/award`, awardForm);
      setAwardForm(EMPTY_AWARD_FORM);
      setAwardOpen(false);
      await loadEvent();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setAwardSaving(false);
    }
  }

  // --- Status transitions ---
  async function handleOpen() {
    if (!confirm('Open this event for bidding?')) return;
    setTransitioning(true);
    setError(null);
    try {
      await api.post(`/sourcing/events/${id}/open`);
      await loadEvent();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setTransitioning(false);
    }
  }

  async function handleClose() {
    if (!confirm('Close bidding for this event?')) return;
    setTransitioning(true);
    setError(null);
    try {
      await api.post(`/sourcing/events/${id}/close`);
      await loadEvent();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setTransitioning(false);
    }
  }

  if (loading) return <p>Loading...</p>;
  if (!event)  return <p style={{ color: 'red' }}>{error || 'Event not found.'}</p>;

  const canAward = event.status === 'open' || event.status === 'evaluating';

  return (
    <div data-testid="sourcing-workspace-page">
      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <h2 data-testid="sourcing-workspace-event-title">{event.title}</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {event.status === 'draft' && (
            <button data-testid="sourcing-event-open" onClick={handleOpen} disabled={transitioning}>
              {transitioning ? 'Opening...' : 'Open for Bidding'}
            </button>
          )}
          {event.status === 'open' && (
            <button data-testid="sourcing-event-close" onClick={handleClose} disabled={transitioning}>
              {transitioning ? 'Closing...' : 'Close Bidding'}
            </button>
          )}
        </div>
      </div>

      {error && <p style={{ color: 'red' }}>{error}</p>}

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1.25rem', borderBottom: '1px solid #ddd' }}>
        {(can('messenger_thread', 'read') ? [...TABS, 'discuss'] : TABS).map((t) => (
          <button
            key={t}
            data-testid={`sourcing-tab-${t}`}
            onClick={() => setTab(t)}
            style={{
              padding: '0.4rem 0.9rem',
              background: tab === t ? '#333' : 'transparent',
              color: tab === t ? '#fff' : 'inherit',
              border: 'none',
              borderBottom: tab === t ? '2px solid #333' : '2px solid transparent',
              cursor: 'pointer',
              textTransform: 'capitalize',
            }}
          >
            {t}
          </button>
        ))}
      </div>

      {/* ---- Discussion ---- */}
      {tab === 'discuss' && (
        <div data-testid="sourcing-discussion">
          <DiscussThread subjectType="sourcing_event" subjectId={id} subjectTitle={event.title || 'Sourcing event'} shareUrl={`/sourcing/events/${id}`} />
        </div>
      )}

      {/* ---- Overview ---- */}
      {tab === 'overview' && (
        <div>
          <table>
            <tbody>
              <tr><th style={{ textAlign: 'left', paddingRight: '1rem' }}>Type</th><td>{event.type}</td></tr>
              <tr><th style={{ textAlign: 'left', paddingRight: '1rem' }}>Status</th><td>{event.status}</td></tr>
              <tr><th style={{ textAlign: 'left', paddingRight: '1rem' }}>Opens</th><td>{event.opens_at?.slice(0, 16)}</td></tr>
              <tr><th style={{ textAlign: 'left', paddingRight: '1rem' }}>Closes</th><td>{event.closes_at?.slice(0, 16)}</td></tr>
            </tbody>
          </table>
          {event.criteria && event.criteria.length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              <h4>Evaluation Criteria</h4>
              <ul>
                {event.criteria.map((c, i) => (
                  <li key={i}>{c.name} — weight: {c.weight}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* ---- Invitations ---- */}
      {tab === 'invitations' && (
        <div>
          <div style={{ marginBottom: '1rem' }}>
            <button data-testid="sourcing-invite-supplier" onClick={() => setInviteOpen(true)}>
              Invite Supplier
            </button>
          </div>
          <table style={{ width: '100%' }}>
            <thead>
              <tr><th>Supplier</th><th>Email</th><th>Status</th><th>Sent At</th></tr>
            </thead>
            <tbody>
              {invitations.map((inv) => (
                <tr key={inv.id} data-testid={`sourcing-invitation-row-${inv.id}`}>
                  <td>{inv.supplier_name || inv.supplier_id}</td>
                  <td>{inv.email}</td>
                  <td>{inv.status}</td>
                  <td>{inv.sent_at?.slice(0, 16)}</td>
                </tr>
              ))}
              {!invitations.length && <tr><td colSpan={4}>No invitations yet.</td></tr>}
            </tbody>
          </table>

          {inviteOpen && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
              <div style={{ background: '#fff', borderRadius: 8, padding: '1.5rem', minWidth: 360 }}>
                <h3>Invite Supplier</h3>
                <form onSubmit={handleInvite}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <input
                      placeholder="Supplier ID"
                      value={inviteForm.supplierId}
                      onChange={(e) => patchInvite('supplierId', e.target.value)}
                      required
                    />
                    <input
                      placeholder="Supplier Name"
                      value={inviteForm.supplierName}
                      onChange={(e) => patchInvite('supplierName', e.target.value)}
                      required
                    />
                    <input
                      placeholder="Email"
                      type="email"
                      value={inviteForm.email}
                      onChange={(e) => patchInvite('email', e.target.value)}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                    <button type="submit" data-testid="sourcing-invite-form-submit" disabled={inviteSaving}>
                      {inviteSaving ? 'Inviting...' : 'Send Invite'}
                    </button>
                    <button type="button" onClick={() => { setInviteOpen(false); }}>Cancel</button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ---- Bids ---- */}
      {tab === 'bids' && (
        <div>
          <div style={{ marginBottom: '1rem' }}>
            <button data-testid="sourcing-bid-add" onClick={() => setBidOpen(true)}>
              Record Bid
            </button>
          </div>
          <table style={{ width: '100%' }}>
            <thead>
              <tr><th>Supplier</th><th>Amount</th><th>Notes</th><th>Submitted</th></tr>
            </thead>
            <tbody>
              {bids.map((b) => (
                <tr key={b.id} data-testid={`sourcing-bid-row-${b.id}`}>
                  <td>{b.supplier_name || b.supplier_id}</td>
                  <td>{b.amount_cents != null ? `$${(b.amount_cents / 100).toFixed(2)}` : '—'}</td>
                  <td>{b.notes}</td>
                  <td>{b.submitted_at?.slice(0, 16)}</td>
                </tr>
              ))}
              {!bids.length && <tr><td colSpan={4}>No bids yet.</td></tr>}
            </tbody>
          </table>

          {bidOpen && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
              <div style={{ background: '#fff', borderRadius: 8, padding: '1.5rem', minWidth: 360 }}>
                <h3>Record Bid</h3>
                <form onSubmit={handleBid}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <input
                      placeholder="Supplier ID"
                      value={bidForm.supplierId}
                      onChange={(e) => patchBid('supplierId', e.target.value)}
                      required
                    />
                    <input
                      placeholder="Amount (cents)"
                      type="number"
                      min={0}
                      value={bidForm.amountCents}
                      onChange={(e) => patchBid('amountCents', e.target.value)}
                      required
                    />
                    <textarea
                      placeholder="Notes"
                      value={bidForm.notes}
                      onChange={(e) => patchBid('notes', e.target.value)}
                      rows={3}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                    <button type="submit" data-testid="sourcing-bid-form-submit" disabled={bidSaving}>
                      {bidSaving ? 'Saving...' : 'Record Bid'}
                    </button>
                    <button type="button" onClick={() => { setBidOpen(false); }}>Cancel</button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ---- Scoreboard ---- */}
      {tab === 'scoreboard' && (
        <div>
          <div style={{ marginBottom: '1rem' }}>
            <button data-testid="sourcing-scoreboard-refresh" onClick={loadScoreboard} disabled={scoreLoading}>
              {scoreLoading ? 'Refreshing...' : 'Refresh Scoreboard'}
            </button>
          </div>
          {scoreLoading ? (
            <p>Loading scoreboard...</p>
          ) : (
            <table style={{ width: '100%' }}>
              <thead>
                <tr><th>Rank</th><th>Supplier</th><th>Score</th><th>Amount</th></tr>
              </thead>
              <tbody>
                {scoreboard.map((s, i) => (
                  <tr key={s.bid_id || s.supplier_id || i} data-testid={`sourcing-scoreboard-row-${s.bid_id || i}`}>
                    <td>{i + 1}</td>
                    <td>{s.supplier_name || s.supplier_id}</td>
                    <td>{s.score != null ? s.score.toFixed(2) : '—'}</td>
                    <td>{s.amount_cents != null ? `$${(s.amount_cents / 100).toFixed(2)}` : '—'}</td>
                  </tr>
                ))}
                {!scoreboard.length && <tr><td colSpan={4}>No scored bids yet.</td></tr>}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ---- Award ---- */}
      {tab === 'award' && (
        <div>
          {canAward ? (
            <>
              <p>Select the winning bid and confirm the award.</p>
              <div style={{ marginBottom: '1rem' }}>
                <select
                  value={awardForm.bidId}
                  onChange={(e) => patchAward('bidId', e.target.value)}
                  style={{ marginRight: '0.5rem' }}
                >
                  <option value="">— select bid —</option>
                  {bids.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.supplier_name || b.supplier_id} — {b.amount_cents != null ? `$${(b.amount_cents / 100).toFixed(2)}` : 'no amount'}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => { if (awardForm.bidId) setAwardOpen(true); }}
                  disabled={!awardForm.bidId}
                >
                  Award
                </button>
              </div>
            </>
          ) : (
            <p>Award is only available when the event is open or in evaluation. Current status: <strong>{event.status}</strong>.</p>
          )}

          {awardOpen && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
              <div style={{ background: '#fff', borderRadius: 8, padding: '1.5rem', minWidth: 360 }}>
                <h3>Confirm Award</h3>
                <p>You are awarding this event to the selected supplier. This action cannot be undone.</p>
                <form onSubmit={handleAward}>
                  <textarea
                    placeholder="Award note (optional)"
                    value={awardForm.note}
                    onChange={(e) => patchAward('note', e.target.value)}
                    rows={3}
                    style={{ width: '100%', marginBottom: '1rem' }}
                  />
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button type="submit" data-testid="sourcing-award-submit" disabled={awardSaving}>
                      {awardSaving ? 'Awarding...' : 'Confirm Award'}
                    </button>
                    <button type="button" onClick={() => setAwardOpen(false)}>Cancel</button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
